/**
 * LastSyncedFooter.tsx
 * Phase 4, Item 9 — "Last synchronized: X mins ago" footer component.
 *
 * Displays the time elapsed since the last successful data refresh.
 * The text refreshes every 10 seconds so it stays accurate between polls.
 *
 * Usage:
 *   <LastSyncedFooter lastSyncedAt={lastSyncedAt} />
 *
 * Matches the footer text shown in the mockups:
 *   - Master Gantt: "Last synchronized: 2 mins ago"
 *   - Employee Schedule: "Data last synchronized: 2 mins ago"
 *
 * Styling: uses theme.palette.text.secondary; no hex literals.
 */

import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { formatLastSynced } from '../../hooks/useAutoRefresh.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LastSyncedFooterProps {
  /** The timestamp of the last successful refresh. Null means never loaded. */
  lastSyncedAt: Date | null;
  /**
   * Optional prefix label displayed before the time-ago string.
   * Defaults to "Last synchronized".
   */
  label?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LastSyncedFooter: React.FC<LastSyncedFooterProps> = ({
  lastSyncedAt,
  label = 'Last synchronized',
}) => {
  // Re-render every 10 s so the "X seconds/mins ago" text stays fresh
  // between the 60-second auto-refresh cycles.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  const timeAgoText = lastSyncedAt
    ? formatLastSynced(new Date(), lastSyncedAt)
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        pt: 0.75,
        pb: 0.5,
      }}
    >
      <SyncIcon sx={{ fontSize: '0.85rem', color: 'text.secondary', opacity: 0.7 }} />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: '0.68rem' }}
      >
        {timeAgoText
          ? `${label}: ${timeAgoText}`
          : `${label}: loading…`}
      </Typography>
    </Box>
  );
};
